import { useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useMasterApprovalScreen } from './hooks/useMasterApprovalScreen'
import AppPagination from '../../../components/AppPagination'
import SharedFilters from '../../../components/SharedFilters'
import ApprovalEmptyState from '../components/ApprovalEmptyState'
import MasterApprovalDiffModal from './components/MasterApprovalDiffModal'

function statusChipClass(status) {
  if (status === 'APPROVED') return 'bg-emerald-100 text-emerald-700'
  if (status === 'REJECTED') return 'bg-error-container text-error'
  if (status === 'SUPERSEDED' || status === 'ARCHIVED') return 'bg-slate-200 text-slate-600'
  return 'bg-primary-fixed text-primary'
}

function actionBadgeStyle(action) {
  if (action === 'CREATE') return 'bg-emerald-100 text-emerald-800'
  if (action === 'UPDATE') return 'bg-blue-100 text-blue-800'
  if (action === 'ARCHIVE' || action === 'DELETE') return 'bg-amber-100 text-amber-800'
  return 'bg-purple-100 text-purple-800'
}

export default function MasterApprovalScreen() {
  const { state, actions } = useMasterApprovalScreen()
  const location = useLocation()

  const statusOptions = [
    { value: 'PENDING', label: 'Pending' },
    { value: 'all', label: 'All Statuses' },
    { value: 'APPROVED', label: 'Approved' },
    { value: 'REJECTED', label: 'Rejected' },
    { value: 'ARCHIVED', label: 'Archived' },
  ]

  const moduleOptions = [
    { value: 'all', label: 'All Master Modules' },
    ...state.modules.map((m) => ({ value: m.key, label: m.label })),
  ]

  const actionOptions = [
    { value: 'all', label: 'All Actions' },
    { value: 'CREATE', label: 'Create' },
    { value: 'UPDATE', label: 'Update' },
    { value: 'ARCHIVE', label: 'Archive' },
    { value: 'RESTORE', label: 'Restore' },
  ]

  const selectableCount = state.rows.filter((r) => r.approvalStatus === 'PENDING' && r.canApprove).length
  const isAllSelected = selectableCount > 0 && state.selectedIds.size >= selectableCount

  return (
    <div className="mx-auto w-full max-w-[1400px] pb-10">
      <header className="mb-8 rounded-[2rem] bg-surface-container-low px-8 py-8">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">Approval Console</p>
        <h1 className="mt-2 text-4xl font-extrabold tracking-tight text-on-surface md:text-5xl">Masters Approval</h1>
        <p className="mt-4 max-w-3xl text-base leading-relaxed text-on-surface-variant md:text-lg">
          Govern pending entity creations, property updates, and lifecycle changes across all organization masters.
        </p>
      </header>

      {/* Filter toolbar */}
      <div className="mb-6">
        <SharedFilters
          showSearch={true}
          searchField="Entity"
          searchText={state.searchText}
          setSearchText={actions.setSearchText}
          searchFieldOptions={[{ value: 'Entity', label: 'Entity or Module' }]}
          showStatus={true}
          statusFilter={state.statusFilter}
          setStatusFilter={actions.setStatusFilter}
          statusOptions={statusOptions}
          visibleAccordionFilters={['module', 'action', 'dateRange']}
          moduleFilter={state.moduleFilter}
          setModuleFilter={actions.setModuleFilter}
          moduleOptions={moduleOptions}
          actionFilter={state.actionFilter}
          setActionFilter={actions.setActionFilter}
          actionOptions={actionOptions}
          fromDate={state.fromDate}
          setFromDate={actions.setFromDate}
          toDate={state.toDate}
          setToDate={actions.setToDate}
          tools={
            state.canDecideMasters
              ? [
                  {
                    key: 'multiSelect',
                    label: state.isMultiSelectMode ? 'Cancel Selection' : 'Select Multiple',
                    icon: state.isMultiSelectMode ? 'close' : 'checklist',
                    iconClassName: state.isMultiSelectMode ? 'text-error' : 'text-primary',
                  },
                ]
              : []
          }
          onToolSelect={(key) => {
            if (key === 'multiSelect') {
              actions.toggleMultiSelectMode()
            }
          }}
        />
      </div>

      {state.error ? (
        <div className="mb-4 rounded-xl border border-error/30 bg-error-container px-4 py-3 text-sm text-error">
          {state.error}
        </div>
      ) : null}

      {state.actionError ? (
        <div className="mb-4 rounded-xl border border-error/30 bg-error-container px-4 py-3 text-sm text-error">
          {state.actionError}
        </div>
      ) : null}

      {state.actionMessage ? (
        <div className="mb-4 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {state.actionMessage}
        </div>
      ) : null}

      <section className="overflow-hidden rounded-3xl bg-surface-container-low relative">
        {state.isMultiSelectMode && (
          <div className="bg-surface-container-highest border-b border-outline-variant/30 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
            <div className="flex items-center gap-4">
              <span className="text-sm font-bold text-on-surface">{state.selectedIds.size} selected</span>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={actions.bulkApprove}
                disabled={state.selectedIds.size === 0}
                className="rounded-full bg-emerald-100 hover:bg-emerald-200 px-6 py-2 text-xs font-bold uppercase tracking-wider text-emerald-700 disabled:opacity-50 transition"
              >
                Approve
              </button>
              <button
                type="button"
                onClick={actions.bulkReject}
                disabled={state.selectedIds.size === 0}
                className="rounded-full bg-error-container hover:bg-error-container/80 px-6 py-2 text-xs font-bold uppercase tracking-wider text-error disabled:opacity-50 transition"
              >
                Reject
              </button>
              <button
                type="button"
                onClick={actions.selectAll}
                disabled={selectableCount === 0}
                className="rounded-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 px-6 py-2 text-xs font-bold uppercase tracking-wider text-white transition shadow-xs"
              >
                {isAllSelected ? 'Deselect All' : 'Select All'}
              </button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1200px] border-separate border-spacing-0 text-left">
            <thead>
              <tr className="bg-surface-container-highest/40">
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Module</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Entity</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Action</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Requested By</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Date</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Status</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant text-center">Actions</th>
                {state.isMultiSelectMode && (
                  <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant text-right">Select</th>
                )}
              </tr>
            </thead>

            <tbody className="divide-y divide-outline-variant/20">
              {state.isLoading ? (
                <tr>
                  <td colSpan={state.isMultiSelectMode ? 8 : 7} className="px-6 py-16 text-center text-sm text-on-surface-variant">
                    Loading masters approval queue...
                  </td>
                </tr>
              ) : !state.error && state.rows.length === 0 ? (
                <tr>
                  <td colSpan={state.isMultiSelectMode ? 8 : 7} className="px-6 py-16 text-center text-sm text-on-surface-variant">
                    <ApprovalEmptyState />
                  </td>
                </tr>
              ) : (
                state.rows.map((row) => {
                  const canApprove = row.approvalStatus === 'PENDING' && row.canApprove
                  const isBusy = state.activeActionId === row.id
                  return (
                    <tr key={row.id} className="transition-colors hover:bg-surface-container-lowest">
                      <td className="px-6 py-5">
                        <span className="font-bold text-primary block">{row.moduleName}</span>
                        <span className="text-[11px] font-mono text-on-surface-variant/70 uppercase">req #{row.id}</span>
                      </td>
                      <td className="px-6 py-5 text-base font-semibold text-on-surface">
                        {row.entityName}
                      </td>
                      <td className="px-6 py-5">
                        <span className={`rounded-full px-2.5 py-1 text-xs font-bold uppercase tracking-wider ${actionBadgeStyle(row.action)}`}>
                          {row.action}
                        </span>
                      </td>
                      <td className="px-6 py-5 text-sm text-on-surface">{row.requestedBy}</td>
                      <td className="px-6 py-5 text-sm text-on-surface">{row.requestedAt}</td>
                      <td className="px-6 py-5">
                        <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider ${statusChipClass(row.approvalStatus)}`}>
                          {row.approvalStatus}
                        </span>
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => actions.openDetailModal(row.id)}
                            className="inline-flex items-center gap-1 rounded-full border border-outline-variant/50 bg-surface-container-high hover:bg-surface-container-highest px-3 py-1.5 text-xs font-bold text-on-surface transition"
                            title="Inspect diff and payload"
                          >
                            <span className="material-symbols-outlined text-sm">visibility</span>
                            Review
                          </button>

                          {!state.isMultiSelectMode && (
                            <>
                              {canApprove && (
                                <>
                                  <button
                                    type="button"
                                    disabled={isBusy}
                                    onClick={() => actions.approveRequest(row.id)}
                                    className="rounded-full bg-emerald-100 hover:bg-emerald-200 px-3.5 py-1.5 text-xs font-bold uppercase tracking-wider text-emerald-700 disabled:opacity-50 transition"
                                  >
                                    {isBusy ? '...' : 'Approve'}
                                  </button>
                                  <button
                                    type="button"
                                    disabled={isBusy}
                                    onClick={() => actions.rejectRequest(row.id)}
                                    className="rounded-full bg-error-container hover:bg-error-container/80 px-3.5 py-1.5 text-xs font-bold uppercase tracking-wider text-error disabled:opacity-50 transition"
                                  >
                                    {isBusy ? '...' : 'Reject'}
                                  </button>
                                </>
                              )}

                              {row.canEdit && row.entryRoute && (
                                <Link
                                  to={`${row.entryRoute}${row.entityId ? `?id=${row.entityId}` : ''}${
                                    row.entryRoute.includes('?') ? '&' : '?'
                                  }returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                                  className="inline-flex items-center gap-1 rounded-full border border-outline-variant/50 bg-surface-container-high hover:bg-surface-container-highest px-3 py-1.5 text-xs font-bold text-primary transition"
                                  title="Edit entry in Master form"
                                >
                                  <span className="material-symbols-outlined text-sm">edit</span>
                                  Edit
                                </Link>
                              )}
                            </>
                          )}
                        </div>
                      </td>

                      {state.isMultiSelectMode && (
                        <td className="px-6 py-5 text-right">
                          {canApprove ? (
                            <input
                              type="checkbox"
                              checked={state.selectedIds.has(row.id)}
                              onChange={() => actions.toggleSelection(row.id)}
                              className="h-5 w-5 rounded border-outline-variant text-primary focus:ring-primary cursor-pointer"
                            />
                          ) : (
                            <input
                              type="checkbox"
                              disabled
                              className="h-5 w-5 rounded border-outline-variant opacity-30 cursor-not-allowed"
                            />
                          )}
                        </td>
                      )}
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 border-t border-outline-variant/20 px-6 py-5">
          <span className="text-sm text-on-surface-variant">
            Showing {state.rangeStart}-{state.rangeEnd} of {state.totalCount} master requests
          </span>

          <AppPagination
            page={state.page}
            totalPages={state.totalPages}
            onPageChange={actions.goToPage}
            onPrevious={actions.goToPreviousPage}
            onNext={actions.goToNextPage}
            canPrevious={state.page > 1}
            canNext={Boolean(state.hasMore) || state.page < state.totalPages}
          />
        </div>
      </section>

      {/* Summary Stat Cards */}
      <section className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-5">
        <button
          type="button"
          onClick={() => actions.setStatusFilter('PENDING')}
          className={`rounded-2xl bg-surface-container-low p-6 text-left transition hover:bg-surface-container-high ${
            state.statusFilter === 'PENDING' ? 'ring-2 ring-primary ring-offset-2' : ''
          }`}
        >
          <p className="text-xs font-bold uppercase tracking-widest text-on-surface-variant">Pending</p>
          <h3 className="mt-2 text-4xl font-black text-on-surface">{state.totals.pending}</h3>
        </button>
        <button
          type="button"
          onClick={() => actions.setStatusFilter('APPROVED')}
          className={`rounded-2xl bg-surface-container-low p-6 text-left transition hover:bg-surface-container-high ${
            state.statusFilter === 'APPROVED' ? 'ring-2 ring-primary ring-offset-2' : ''
          }`}
        >
          <p className="text-xs font-bold uppercase tracking-widest text-on-surface-variant">Approved</p>
          <h3 className="mt-2 text-4xl font-black text-on-surface">{state.totals.approved}</h3>
        </button>
        <button
          type="button"
          onClick={() => actions.setStatusFilter('REJECTED')}
          className={`rounded-2xl bg-surface-container-low p-6 text-left transition hover:bg-surface-container-high ${
            state.statusFilter === 'REJECTED' ? 'ring-2 ring-primary ring-offset-2' : ''
          }`}
        >
          <p className="text-xs font-bold uppercase tracking-widest text-on-surface-variant">Rejected</p>
          <h3 className="mt-2 text-4xl font-black text-on-surface">{state.totals.rejected}</h3>
        </button>
        <button
          type="button"
          onClick={() => actions.setStatusFilter('ARCHIVED')}
          className={`rounded-2xl bg-surface-container-low p-6 text-left transition hover:bg-surface-container-high ${
            state.statusFilter === 'ARCHIVED' ? 'ring-2 ring-primary ring-offset-2' : ''
          }`}
        >
          <p className="text-xs font-bold uppercase tracking-widest text-on-surface-variant">Archived</p>
          <h3 className="mt-2 text-4xl font-black text-on-surface">{state.totals.archived}</h3>
        </button>
        <button
          type="button"
          onClick={() => actions.setStatusFilter('all')}
          className={`rounded-2xl bg-primary p-6 text-left text-on-primary transition hover:opacity-95 ${
            state.statusFilter === 'all' ? 'ring-2 ring-primary ring-offset-2' : ''
          }`}
        >
          <p className="text-xs font-bold uppercase tracking-widest text-primary-fixed-dim">Total Queue (View)</p>
          <h3 className="mt-2 text-4xl font-black">{state.totalCount}</h3>
        </button>
      </section>

      {/* Diff / Review modal */}
      <MasterApprovalDiffModal
        isOpen={Boolean(state.selectedDetail)}
        detail={state.selectedDetail}
        isLoading={state.isDetailLoading}
        error={state.detailError}
        returnUrl={location.pathname + location.search}
        onClose={actions.closeDetailModal}
        onApprove={(id, reason) => actions.approveRequest(id, reason)}
        onReject={(id, reason) => actions.rejectRequest(id, reason)}
        isBusy={state.activeActionId === state.selectedDetail?.id}
      />
    </div>
  )
}
